import { FunctionComponent, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

const MAX_MESSAGE_LENGTH = 140

const Announcements: FunctionComponent = () => {
  const styles = useStyles()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [enabled, setEnabled] = useState(false)
  const [message, setMessage] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/announcement_settings')
        setEnabled(!!res.data.enabled)
        setMessage(res.data.message || '')
        setLinkUrl(res.data.linkUrl || '')
        setLinkText(res.data.linkText || '')
      } catch {
        toast.error('Failed to load announcement settings')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // A link only makes sense with both a URL and label -- keep the pair in
  // sync so users can't save one without the other and get rejected.
  const linkIsIncomplete = !!linkUrl.trim() !== !!linkText.trim()
  const hasLink = !!linkUrl.trim() && !!linkText.trim()

  const isFormValid = () => {
    if (enabled && !message.trim()) return false
    if (linkIsIncomplete) return false
    return true
  }

  const handleSave = async () => {
    if (!isFormValid()) {
      toast.error(
        enabled && !message.trim()
          ? 'Add a message before enabling the announcement'
          : 'A link needs both a URL and button text, or neither'
      )
      return
    }

    setSaving(true)
    try {
      const res = await axios.post('/organization/update_announcement_settings', {
        enabled,
        message,
        link_url: linkUrl || null,
        link_text: linkText || null,
      })
      setEnabled(!!res.data.enabled)
      setMessage(res.data.message || '')
      setLinkUrl(res.data.linkUrl || '')
      setLinkText(res.data.linkText || '')
      toast.success('Announcement settings saved')
    } catch (error: any) {
      const apiErrors = error.response?.data?.errors
      toast.error(Array.isArray(apiErrors) ? apiErrors.join(', ') : 'Failed to save announcement settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box className={styles.container}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Typography variant="h4" gutterBottom>
        Announcements
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Show a message to your members at the top of their VIP page. Only one announcement can be active at a time.
      </Typography>

      <Box className={styles.layout}>
        <Card className={styles.card}>
          <CardContent>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
              label={enabled ? 'Announcement is live' : 'Announcement is hidden'}
              className={styles.field}
            />

            <TextField
              label="Message"
              variant="outlined"
              fullWidth
              multiline
              minRows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
              className={styles.field}
            />

            <TextField
              label="Link URL (optional)"
              placeholder="https://example.com/event"
              variant="outlined"
              fullWidth
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className={styles.field}
            />

            <TextField
              label="Link button text (optional)"
              placeholder="Learn more"
              variant="outlined"
              fullWidth
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              error={linkIsIncomplete}
              helperText={linkIsIncomplete ? 'Set both a URL and button text, or leave both blank' : ' '}
              className={styles.field}
            />

            <Box className={styles.actions}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || !isFormValid()}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Save
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box className={styles.previewColumn}>
          {/* Plain elements, not Typography: MUI v5's emotion styles override
              makeStyles classes, so font sizes set here would be discarded. */}
          <div className={styles.previewLabel}>Preview — first card on the VIP page</div>
          <Box className={styles.phoneFrame}>
            <Box className={styles.previewCard}>
              <div className={styles.previewTitle}>📣 Announcement</div>
              <div className={styles.previewMessage}>
                {message.trim() || 'Your announcement message will appear here.'}
              </div>
              {hasLink && <span className={styles.previewPill}>{linkText}</span>}
            </Box>
          </Box>
          {!enabled && (
            <div className={styles.previewHiddenNote}>
              Hidden — flip the switch on to show this on the VIP page.
            </div>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    padding: SPACING * 2,
  },
  layout: {
    display: 'flex',
    gap: SPACING * 3,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  card: {
    maxWidth: 480,
    flex: '1 1 400px',
  },
  field: {
    marginBottom: SPACING * 2,
  },
  actions: {
    display: 'flex',
    gap: SPACING * 2,
  },
  previewColumn: {
    flex: '0 0 360px',
    maxWidth: 360,
  },
  previewLabel: {
    marginBottom: SPACING,
    fontSize: 13,
    fontWeight: 600,
    color: colors.GREY_3,
  },
  // Stands in for the VIP page the card sits on.
  phoneFrame: {
    padding: SPACING * 2,
    borderRadius: 16,
    backgroundColor: colors.GREY_7,
    border: `1px solid ${colors.GREY_5}`,
  },
  previewCard: {
    borderRadius: 12,
    padding: SPACING * 2,
    backgroundColor: colors.STITCH_BLUE,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  previewTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.WHITE,
    lineHeight: 1.3,
  },
  previewMessage: {
    fontSize: 14,
    color: colors.WHITE,
    lineHeight: 1.45,
    // Long words and URLs must not blow out the card.
    overflowWrap: 'anywhere',
  },
  previewPill: {
    alignSelf: 'flex-start',
    marginTop: 6,
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 20,
    padding: '5px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: colors.WHITE,
    whiteSpace: 'nowrap',
  },
  previewHiddenNote: {
    marginTop: SPACING,
    fontSize: 12,
    lineHeight: 1.4,
    color: colors.GREY_3,
  },
})

export default Announcements
